// Port of TrailData/Import/FileImporter.swift. The PWA has no file URLs: the UI reads a `File` (picker, drop, share
// target) and passes its text and name (`importFileObject` does that for a `File`).

import type { DataProvenance, EpochMs } from '../core/models';
import { TrailDataError, errorMessage } from '../data/errors';
import { GPXConverter } from '../gpx/converter';
import { GPXError } from '../gpx/errors';
import { GPXParser } from '../gpx/parser';
import { GeoJSONImporter } from './geojson';
import type { ImportedGeoData } from './importedGeoData';
import { KMLImporter } from './kml';

/** Largest accepted file, in bytes. */
export const maximumFileSize = 100_000_000;

/** Supported file extensions (lower case). */
export const supportedExtensions: ReadonlySet<string> = new Set(['gpx', 'geojson', 'json', 'kml']);

type Format = 'gpx' | 'kml' | 'geojson';

/** Swift `URL.pathExtension`: the text after the last `.` of the last path component ('' for none or a dotfile). */
export function pathExtension(fileName: string): string {
  const last = fileName.split('/').filter((c) => c !== '').pop() ?? '';
  const dot = last.lastIndexOf('.');
  return dot <= 0 ? '' : last.slice(dot + 1);
}

/**
 * Format of a file without extension, from its first characters (TS addition: files shared from some Android apps or
 * content URIs arrive without a name extension, which Swift's document picker never produces).
 */
export function sniffFormat(text: string): Format | undefined {
  const head = text.slice(0, 2048).replace(/^﻿/, '').trimStart();
  if (head.startsWith('{')) return 'geojson';
  if (!head.startsWith('<')) return undefined;
  const root = /<([A-Za-z_][\w.-]*:)?(gpx|kml)[\s>/]/.exec(head.replace(/<\?[\s\S]*?\?>|<!--[\s\S]*?-->|<![^>]*>/g, ''));
  return root ? (root[2] as Format) : undefined;
}

/** UTF-8 size of a string, in bytes. */
function utf8Size(text: string): number {
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length && (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}

/**
 * Imports a geodata file, choosing the importer by file extension: `gpx` (`GPXParser` + `GPXConverter`), `geojson` /
 * `json` (`GeoJSONImporter`), `kml` (`KMLImporter`). A file without extension is recognised by its content.
 *
 * @param text The file content (e.g. `await file.text()`).
 * @param fileName The file name (provenance, ids, fallback names, format).
 * @param provenance Provenance for all records; defaults to `userImportProvenance(fileName, importedAt)`.
 * @throws TrailDataError only: `unsupportedFormat` (extension, KMZ, not GPX…), `decoding` (too large or malformed),
 *   `noGeometry`.
 */
export function importFile(text: string, fileName: string, importedAt: EpochMs, provenance?: DataProvenance): ImportedGeoData {
  const ext = pathExtension(fileName).toLowerCase();
  let format: Format | undefined;
  if (ext === '') {
    format = sniffFormat(text);
    if (!format) throw TrailDataError.unsupportedFormat('file without extension');
  } else if (!supportedExtensions.has(ext)) {
    throw TrailDataError.unsupportedFormat(ext);
  } else {
    format = ext === 'gpx' ? 'gpx' : ext === 'kml' ? 'kml' : 'geojson';
  }
  const size = utf8Size(text);
  if (size > maximumFileSize) throw TrailDataError.decoding(fileName, `file too large (${size} bytes)`);

  switch (format) {
    case 'gpx':
      return importGPX(text, fileName, importedAt, provenance);
    case 'kml':
      return new KMLImporter().importData(text, fileName, importedAt, provenance);
    case 'geojson':
      return new GeoJSONImporter().importData(text, fileName, importedAt, provenance);
  }
}

/** Reads a browser `File` and imports it (unreadable files throw `TrailDataError` `decoding`). */
export async function importFileObject(
  file: Pick<File, 'name' | 'size' | 'text'>,
  importedAt: EpochMs,
  provenance?: DataProvenance,
): Promise<ImportedGeoData> {
  const ext = pathExtension(file.name).toLowerCase();
  if (ext !== '' && !supportedExtensions.has(ext)) throw TrailDataError.unsupportedFormat(ext);
  if (file.size > maximumFileSize) throw TrailDataError.decoding(file.name, `file too large (${file.size} bytes)`);
  let text: string;
  try {
    text = await file.text();
  } catch (e) {
    throw TrailDataError.decoding(file.name, `unreadable: ${errorMessage(e)}`);
  }
  return importFile(text, file.name, importedAt, provenance);
}

/** GPX via the GPX module: one imported trail, one stage per track (or route), `<wpt>`s as waypoints. */
export function importGPX(
  data: string | Uint8Array,
  fileName: string,
  importedAt: EpochMs,
  provenance?: DataProvenance,
): ImportedGeoData {
  try {
    const document = new GPXParser(maximumFileSize).parse(data);
    const { trail, stages, waypoints } = GPXConverter.importedTrail(document, fileName, importedAt);
    if (provenance) {
      trail.provenance = provenance;
      for (const stage of stages) stage.provenance = provenance;
    }
    return { trails: [trail], stages, waypoints, pointsOfInterest: [], protectedAreas: [], warnings: [] };
  } catch (e) {
    if (!(e instanceof GPXError)) throw e;
    const reason = e.reason;
    switch (reason.kind) {
      case 'noGeometry':
        throw TrailDataError.noGeometry();
      case 'notGPX':
        throw TrailDataError.unsupportedFormat('XML document is not GPX');
      case 'malformedXML':
        throw TrailDataError.decoding(fileName, `line ${reason.line}: ${reason.message}`);
      case 'invalidCoordinate':
        throw TrailDataError.decoding(fileName, `line ${reason.line}: invalid coordinate`);
      case 'fileTooLarge':
        throw TrailDataError.decoding(fileName, `file too large (${reason.bytes} bytes)`);
    }
  }
}

/** The Swift enum as a namespace object. */
export const FileImporter = { maximumFileSize, supportedExtensions, importFile, importFileObject, importGPX } as const;
